import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import {
  Shield, ShieldCheck, ShieldAlert, Settings, Lock, Unlock,
  DollarSign, Users, Clock, CheckCircle, XCircle, AlertTriangle,
  TrendingUp, Activity, FileText, Zap, ChevronDown, ChevronRight,
  BarChart3, Eye, Trash2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

function SliderField({ label, description, value, onChange, min, max, step = 1, prefix = '', suffix = '' }) {
  return (
    <div data-testid={`field-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-white">{label}</label>
        <span className="text-sm font-bold text-blue-400">{prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</span>
      </div>
      {description && <p className="text-[10px] text-gray-500 mb-2">{description}</p>}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-blue-500"
      />
      <div className="flex justify-between text-[9px] text-gray-600 mt-1">
        <span>{prefix}{min.toLocaleString()}{suffix}</span>
        <span>{prefix}{max.toLocaleString()}{suffix}</span>
      </div>
    </div>
  );
}

function CaveatBadge({ label, passed, reason }) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${
        passed ? 'bg-green-500/5 border-green-500/20 text-green-400' : 'bg-red-500/5 border-red-500/20 text-red-400'
      }`}
      data-testid={`caveat-${label}`}
    >
      {passed ? <CheckCircle className="w-3.5 h-3.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <span className="font-medium">{label}</span>
        {reason && <span className="text-gray-500 ml-1.5">— {reason}</span>}
      </div>
    </div>
  );
}

export default function LenderProtection() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [delegation, setDelegation] = useState(null);
  const [hasDelegation, setHasDelegation] = useState(false);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const [scope, setScope] = useState({
    max_per_loan: 25000,
    max_per_day: 50000,
    max_per_week: 200000,
    max_total_pool: 500000,
    min_credit_score: 550,
    max_concurrent_loans: 5,
    collateral_required: false,
    min_collateral_ratio: 1.2,
    allowed_purposes: [],
    auto_revoke_on_default: true,
  });
  const [label, setLabel] = useState('Default Protection Policy');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(() => {
    const fetches = [
      fetch(`${API}/api/delegations/overview`).then(r => r.json()),
    ];
    if (token) {
      fetches.push(
        fetch(`${API}/api/delegations/my`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      );
    }
    Promise.all(fetches).then(([ov, my]) => {
      setOverview(ov);
      if (my?.has_delegation && my.delegation) {
        setDelegation(my.delegation);
        setHasDelegation(true);
        setScope(my.delegation.scope);
        setLabel(my.delegation.label || 'Default Protection Policy');
      }
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = hasDelegation
        ? `${API}/api/delegations/${delegation.id}`
        : `${API}/api/delegations`;
      const method = hasDelegation ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify({ scope, label }) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      toast({ title: hasDelegation ? 'Protection Updated' : 'Protection Activated', description: 'Your lending pool is now protected by ERC-7710 scoped delegations.' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!delegation) return;
    try {
      await fetch(`${API}/api/delegations/${delegation.id}`, { method: 'DELETE', headers });
      toast({ title: 'Protection Revoked', description: 'Your delegation has been permanently revoked.' });
      setDelegation(null);
      setHasDelegation(false);
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleTogglePause = async () => {
    if (!delegation) return;
    const newStatus = delegation.status === 'active' ? 'paused' : 'active';
    try {
      await fetch(`${API}/api/delegations/${delegation.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ status: newStatus }),
      });
      toast({ title: newStatus === 'active' ? 'Protection Resumed' : 'Protection Paused' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const updateScope = (key, val) => setScope(prev => ({ ...prev, [key]: val }));

  return (
    <DashboardLayout title="Lender Protection">
      <div data-testid="lender-protection-page">

      {/* Hero */}
      <section className="pb-8">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6"
          >
            <Shield className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-300">ERC-7710 Scoped Delegation</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight mb-4"
          >
            Lender{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
              Protection
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-sm text-gray-400 max-w-2xl mx-auto"
          >
            Your funds, your rules — enforced by code, not promises. Set exactly how much can leave your pool, to whom, how often, and under what conditions.
          </motion.p>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="max-w-5xl mx-auto pb-16">

          {/* Platform Stats Banner */}
          {overview && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8"
              data-testid="delegation-overview"
            >
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                <div className="text-lg font-bold text-white">{overview.active_delegations}</div>
                <div className="text-[10px] text-gray-500">Active Policies</div>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                <div className="text-lg font-bold text-green-400">{overview.protected_lenders}</div>
                <div className="text-[10px] text-gray-500">Protected Lenders</div>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                <div className="text-lg font-bold text-blue-400">${(overview.total_pool_capacity / 1000).toFixed(0)}K</div>
                <div className="text-[10px] text-gray-500">Total Pool Capacity</div>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 text-center">
                <div className="text-lg font-bold text-cyan-400">{overview.active_caveats}</div>
                <div className="text-[10px] text-gray-500">Active Caveats</div>
              </div>
            </motion.div>
          )}

          <div className="grid lg:grid-cols-5 gap-6">
            {/* Configuration Panel (3 cols) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-3 space-y-5"
            >
              {/* Status Banner */}
              {hasDelegation && delegation && (
                <div className={`flex items-center justify-between p-4 rounded-xl border ${
                  delegation.status === 'active'
                    ? 'bg-green-500/5 border-green-500/20'
                    : 'bg-yellow-500/5 border-yellow-500/20'
                }`} data-testid="delegation-status">
                  <div className="flex items-center gap-3">
                    {delegation.status === 'active' ? (
                      <ShieldCheck className="w-5 h-5 text-green-400" />
                    ) : (
                      <ShieldAlert className="w-5 h-5 text-yellow-400" />
                    )}
                    <div>
                      <span className="text-sm font-medium">{delegation.label}</span>
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        delegation.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {delegation.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={handleTogglePause} className="border-white/10 text-xs h-7" data-testid="toggle-pause-btn">
                      {delegation.status === 'active' ? <><Lock className="w-3 h-3 mr-1" /> Pause</> : <><Unlock className="w-3 h-3 mr-1" /> Resume</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleRevoke} className="border-red-500/20 text-red-400 text-xs h-7 hover:bg-red-500/10" data-testid="revoke-btn">
                      <Trash2 className="w-3 h-3 mr-1" /> Revoke
                    </Button>
                  </div>
                </div>
              )}

              {/* Policy Name */}
              <div className="rounded-xl border border-white/5 bg-white/[0.02] p-5" data-testid="policy-name-section">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-gray-400" /> Policy Name
                </h3>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="My Lending Policy"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50"
                  data-testid="policy-name-input"
                />
              </div>

              {/* Spending Limits */}
              <div className="rounded-xl border border-blue-500/10 bg-blue-500/[0.02] p-5 space-y-5" data-testid="spending-limits">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-blue-400" /> Spending Limits
                </h3>
                <SliderField label="Max Per Loan" description="Maximum amount for any single loan" value={scope.max_per_loan} onChange={v => updateScope('max_per_loan', v)} min={1000} max={200000} step={1000} prefix="$" />
                <SliderField label="Daily Limit" description="Max total disbursement per day" value={scope.max_per_day} onChange={v => updateScope('max_per_day', v)} min={5000} max={500000} step={5000} prefix="$" />
                <SliderField label="Weekly Limit" description="Max total disbursement per week" value={scope.max_per_week} onChange={v => updateScope('max_per_week', v)} min={10000} max={2000000} step={10000} prefix="$" />
                <SliderField label="Total Pool Budget" description="Lifetime lending capacity" value={scope.max_total_pool} onChange={v => updateScope('max_total_pool', v)} min={10000} max={10000000} step={10000} prefix="$" />
              </div>

              {/* Risk Controls */}
              <div className="rounded-xl border border-orange-500/10 bg-orange-500/[0.02] p-5 space-y-5" data-testid="risk-controls">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400" /> Risk Controls
                </h3>
                <SliderField label="Min Credit Score" description="AI-powered borrower credit threshold" value={scope.min_credit_score} onChange={v => updateScope('min_credit_score', v)} min={300} max={850} step={10} />
                <SliderField label="Max Concurrent Loans" description="Active loans from your pool at once" value={scope.max_concurrent_loans} onChange={v => updateScope('max_concurrent_loans', v)} min={1} max={20} />

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium">Require Collateral</span>
                    <p className="text-[10px] text-gray-500">Only fund loans backed by collateral</p>
                  </div>
                  <button
                    onClick={() => updateScope('collateral_required', !scope.collateral_required)}
                    className={`w-10 h-5 rounded-full transition-colors ${scope.collateral_required ? 'bg-blue-500' : 'bg-white/10'}`}
                    data-testid="collateral-toggle"
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${scope.collateral_required ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {scope.collateral_required && (
                  <SliderField label="Min Collateral Ratio" description="e.g. 1.5 = 150% collateral required" value={scope.min_collateral_ratio} onChange={v => updateScope('min_collateral_ratio', v)} min={1.0} max={3.0} step={0.1} suffix="x" />
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium">Auto-Revoke on Default</span>
                    <p className="text-[10px] text-gray-500">Block borrowers with any past default</p>
                  </div>
                  <button
                    onClick={() => updateScope('auto_revoke_on_default', !scope.auto_revoke_on_default)}
                    className={`w-10 h-5 rounded-full transition-colors ${scope.auto_revoke_on_default ? 'bg-blue-500' : 'bg-white/10'}`}
                    data-testid="auto-revoke-toggle"
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${scope.auto_revoke_on_default ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 h-12 text-sm font-medium"
                data-testid="save-delegation-btn"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : hasDelegation ? (
                  <><ShieldCheck className="w-4 h-4 mr-2" /> Update Protection Policy</>
                ) : (
                  <><Shield className="w-4 h-4 mr-2" /> Activate Protection</>
                )}
              </Button>
            </motion.div>

            {/* Live Stats Panel (2 cols) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2 space-y-5"
            >
              {/* Pool Health */}
              {hasDelegation && delegation && (
                <div className="rounded-xl border border-green-500/10 bg-green-500/[0.02] p-5" data-testid="pool-health">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-green-400" /> Pool Health
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-500">Pool Utilization</span>
                        <span className="text-white font-medium">{delegation.live_stats?.pool_utilization || 0}%</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full transition-all" style={{ width: `${delegation.live_stats?.pool_utilization || 0}%` }} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-3 rounded-lg bg-white/[0.03]">
                        <div className="text-[10px] text-gray-500">Daily Left</div>
                        <div className="text-sm font-bold text-white">${(delegation.live_stats?.daily_remaining || 0).toLocaleString()}</div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03]">
                        <div className="text-[10px] text-gray-500">Weekly Left</div>
                        <div className="text-sm font-bold text-white">${(delegation.live_stats?.weekly_remaining || 0).toLocaleString()}</div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03]">
                        <div className="text-[10px] text-gray-500">Loans Funded</div>
                        <div className="text-sm font-bold text-green-400">{delegation.stats?.loans_funded || 0}</div>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.03]">
                        <div className="text-[10px] text-gray-500">Loans Blocked</div>
                        <div className="text-sm font-bold text-red-400">{delegation.stats?.loans_rejected || 0}</div>
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-white/[0.03]">
                      <div className="text-[10px] text-gray-500">Total Disbursed</div>
                      <div className="text-lg font-bold text-white">${(delegation.stats?.total_disbursed || 0).toLocaleString()} <span className="text-xs text-gray-500">/ ${(delegation.scope?.max_total_pool || 0).toLocaleString()}</span></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Active Caveats */}
              <div className="rounded-xl border border-white/5 bg-white/[0.02] p-5" data-testid="active-caveats">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-400" /> Active Caveats
                </h3>
                <div className="space-y-2 text-xs">
                  <CaveatBadge label="Per-Loan Limit" passed={true} reason={`$${scope.max_per_loan.toLocaleString()}`} />
                  <CaveatBadge label="Daily Cap" passed={true} reason={`$${scope.max_per_day.toLocaleString()}/day`} />
                  <CaveatBadge label="Weekly Cap" passed={true} reason={`$${scope.max_per_week.toLocaleString()}/week`} />
                  <CaveatBadge label="Pool Budget" passed={true} reason={`$${scope.max_total_pool.toLocaleString()} total`} />
                  <CaveatBadge label="Credit Gate" passed={true} reason={`Min score: ${scope.min_credit_score}`} />
                  <CaveatBadge label="Concurrent Limit" passed={true} reason={`Max ${scope.max_concurrent_loans} loans`} />
                  {scope.collateral_required && (
                    <CaveatBadge label="Collateral" passed={true} reason={`Min ${scope.min_collateral_ratio}x ratio`} />
                  )}
                  <CaveatBadge label="Double-Dip Guard" passed={true} reason="1 loan per borrower" />
                  {scope.auto_revoke_on_default && (
                    <CaveatBadge label="Default Block" passed={true} reason="Auto-revoke on default" />
                  )}
                </div>
              </div>

              {/* ERC-7710 Info */}
              <div className="rounded-xl border border-cyan-500/10 bg-cyan-500/[0.02] p-5" data-testid="erc7710-info">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-cyan-400" /> ERC-7710 Standard
                </h3>
                <div className="space-y-2 text-xs text-gray-400">
                  <p>Scoped delegations enable programmable, on-chain permission control for lending funds.</p>
                  <div className="space-y-1.5 mt-3">
                    <div className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-cyan-400" /> Spending limits enforced per-transaction</div>
                    <div className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-cyan-400" /> AI credit score gating at disbursement</div>
                    <div className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-cyan-400" /> Double-dip prevention built-in</div>
                    <div className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-cyan-400" /> Full audit trail for every decision</div>
                    <div className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-cyan-400" /> Merged into x402 by Coinbase</div>
                  </div>
                </div>
              </div>

              {/* Audit Trail */}
              {hasDelegation && delegation?.recent_audit?.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="audit-trail">
                  <button
                    onClick={() => setShowAudit(!showAudit)}
                    className="w-full p-4 flex items-center justify-between hover:bg-white/[0.01] transition-colors"
                  >
                    <span className="text-sm font-semibold flex items-center gap-2">
                      <Eye className="w-4 h-4 text-gray-400" /> Audit Trail
                    </span>
                    {showAudit ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  {showAudit && (
                    <div className="border-t border-white/5 divide-y divide-white/[0.04]">
                      {delegation.recent_audit.map((entry) => (
                        <div key={entry.id} className="px-4 py-2.5 flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                            entry.action === 'created' ? 'bg-blue-500/10 text-blue-400' :
                            entry.action === 'disbursement' ? 'bg-green-500/10 text-green-400' :
                            entry.action === 'rejected' ? 'bg-red-500/10 text-red-400' :
                            'bg-gray-500/10 text-gray-400'
                          }`}>
                            {entry.action === 'created' ? <Shield className="w-3 h-3" /> :
                             entry.action === 'disbursement' ? <DollarSign className="w-3 h-3" /> :
                             entry.action === 'rejected' ? <XCircle className="w-3 h-3" /> :
                             <FileText className="w-3 h-3" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium capitalize">{entry.action}</span>
                            {entry.amount > 0 && <span className="text-xs text-gray-500 ml-1.5">${entry.amount.toLocaleString()}</span>}
                          </div>
                          <span className="text-[10px] text-gray-600">{new Date(entry.timestamp).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}

      </div>
    </DashboardLayout>
  );
}
